import { useContext } from "react";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";
import { JointBillContext } from "@features/JointBillList/utils";
import { IconButton } from "@components/IconButton/IconButton";
import {
  BinIcon,
  DocumentArrowIcon,
  DocumentCheckIcon,
  DocumentIcon,
  ExclamationCircleIcon,
  Pencil16Icon,
  ReceiptIcon,
  RefreshIcon,
  RightLeftArrowIcon,
  ShieldIcon,
  ShieldStrikethroughIcon,
} from "@components/icons";
import { Tooltip } from "@components/Tooltip/Tooltip";
import { useFeatureConfig } from "@hooks/useFeatureFlags";

interface IProviderBillNameCellProps {
  name: JSX.Element | string;
  statusBadge?: JSX.Element | string;
  providerBadge?: JSX.Element | string;
  isEditProviderEnabled?: boolean;
  onEditProviderClick?: () => void;
  isRemoveProviderEnabled?: boolean;
  isRemoveProviderLoading?: boolean;
  onRemoveProviderClick?: () => void;
  shouldDisplayRemoveButton?: boolean;
  removeButtonTooltipContent?: string;
  onProviderBillClick?: () => void;
  isProviderBillButtonEnabled?: boolean;
  onProviderBookletClick?: () => void;
  isProviderBookletButtonEnabled?: boolean;
  shouldDisplayRestoreActions?: boolean;
  onRestoreProviderClick?: () => void;
  isBillCarryOverEnabled?: boolean;
  onBillCarryOverClick?: () => void;
  isBillFileAvailable?: boolean;
  isBillFileLoading?: boolean;
  onBillFileClick?: () => void;
  isInsuranceEnabled?: boolean;
  isInsuranceAvailable?: boolean;
  isInsuranceFilesLoading?: boolean;
  onInsuranceButtonClick?: () => void;
  isCertFileAvailable?: boolean;
  isCertFileLoading?: boolean;
  onCertFileClick?: () => void;
  isTransferAvailable?: boolean;
  onTransferClick?: () => void;
}

export const ProviderBillNameCell: React.FC<IProviderBillNameCellProps> = ({
  name,
  statusBadge,
  providerBadge,
  isEditProviderEnabled,
  onEditProviderClick,
  isRemoveProviderEnabled,
  isRemoveProviderLoading,
  shouldDisplayRemoveButton,
  removeButtonTooltipContent,
  onRemoveProviderClick,
  onProviderBillClick,
  isProviderBillButtonEnabled,
  onProviderBookletClick,
  isProviderBookletButtonEnabled,
  shouldDisplayRestoreActions,
  onRestoreProviderClick,
  isBillCarryOverEnabled,
  onBillCarryOverClick,
  isBillFileAvailable,
  isBillFileLoading,
  onBillFileClick,
  isInsuranceEnabled,
  isInsuranceAvailable,
  isInsuranceFilesLoading,
  onInsuranceButtonClick,
  isCertFileAvailable,
  isCertFileLoading,
  onCertFileClick,
  isTransferAvailable,
  onTransferClick,
}) => {
  const { t } = useTranslation();
  const { isSummaryMode } = useContext(JointBillContext);
  const { isDueDateEnabled } = useFeatureConfig();

  return (
    <div className="flex h-full flex-row items-start md:items-center">
      <div className="flex min-w-0 gap-3">
        {providerBadge && (
          <div className="relative size-9 shrink-0">
            <div className="absolute inset-1/2 size-9 -translate-x-1/2 -translate-y-1/2">
              {providerBadge}
            </div>
          </div>
        )}
        <div
          className={twMerge(
            "flex min-w-0 flex-1 flex-shrink flex-col gap-0.5",
            isDueDateEnabled && !providerBadge && "pl-12",
          )}
        >
          <span
            className="truncate text-sm leading-6 md:my-auto md:text-base"
            title={typeof name === "string" ? name : ""}
          >
            {name}
          </span>
          <div className="md:hidden">{statusBadge}</div>
        </div>
        {!isSummaryMode && (
          <div className="hidden gap-2 md:flex">
            {shouldDisplayRestoreActions && (
              <Tooltip
                content={t("bill.interactions.providerWillBeRemoved.tooltip")}
              >
                <div className="flex h-full items-center align-middle">
                  <ExclamationCircleIcon className="size-4 text-red-dark" />
                </div>
              </Tooltip>
            )}
            {shouldDisplayRestoreActions && (
              <Tooltip
                content={t(
                  "bill.interactions.providerWillBeRemoved.restoreTooltip",
                )}
              >
                {(props) => (
                  <IconButton
                    {...props}
                    icon={RefreshIcon}
                    iconClassName="w-4 h-4"
                    onClick={onRestoreProviderClick}
                    aria-label={t(
                      "bill.interactions.providerWillBeRemoved.restoreTooltip",
                    )}
                  />
                )}
              </Tooltip>
            )}
            {isEditProviderEnabled && (
              <Tooltip content={t("bill.interactions.editProvider.tooltip")}>
                {(props) => (
                  <IconButton
                    {...props}
                    icon={Pencil16Icon}
                    iconClassName="w-4 h-4"
                    onClick={onEditProviderClick}
                    aria-label={t("bill.interactions.editProvider.tooltip")}
                  />
                )}
              </Tooltip>
            )}
            {isBillFileAvailable && (
              <Tooltip
                content={t("bill.interactions.downloadBillFile.tooltip")}
              >
                {(props) => (
                  <IconButton
                    isLoading={isBillFileLoading}
                    {...props}
                    icon={DocumentIcon}
                    iconClassName="w-4 h-4"
                    onClick={onBillFileClick}
                    aria-label={t("bill.interactions.downloadBillFile.tooltip")}
                  />
                )}
              </Tooltip>
            )}
            {isTransferAvailable && (
              <Tooltip content={t("bill.interactions.transfer.label")}>
                {(props) => (
                  <IconButton
                    {...props}
                    icon={DocumentArrowIcon}
                    iconClassName="w-4 h-4"
                    onClick={onTransferClick}
                    aria-label={t("bill.interactions.transfer.label")}
                  />
                )}
              </Tooltip>
            )}
            {isCertFileAvailable && (
              <Tooltip
                content={t("bill.interactions.downloadCertFile.tooltip")}
              >
                {(props) => (
                  <IconButton
                    isLoading={isCertFileLoading}
                    {...props}
                    icon={DocumentIcon}
                    iconClassName="w-4 h-4"
                    onClick={onCertFileClick}
                  />
                )}
              </Tooltip>
            )}
            {shouldDisplayRemoveButton && (
              <Tooltip content={removeButtonTooltipContent}>
                {(props) => (
                  <IconButton
                    disabled={!isRemoveProviderEnabled}
                    {...props}
                    icon={BinIcon}
                    iconClassName="size-4 group-hover:text-red-dark"
                    onClick={onRemoveProviderClick}
                    isLoading={isRemoveProviderLoading}
                    aria-label={removeButtonTooltipContent}
                  />
                )}
              </Tooltip>
            )}
            {isBillCarryOverEnabled && (
              <Tooltip content={t("bill.interactions.billCarryOver.tooltip")}>
                {(props) => (
                  <IconButton
                    {...props}
                    icon={RightLeftArrowIcon}
                    iconClassName="w-4 h-4"
                    onClick={onBillCarryOverClick}
                    aria-label={t("bill.interactions.billCarryOver.tooltip")}
                  />
                )}
              </Tooltip>
            )}
            {isProviderBillButtonEnabled && (
              <Tooltip content={t("bill.interactions.providerBill.label")}>
                {(props) => (
                  <IconButton
                    {...props}
                    icon={ReceiptIcon}
                    iconClassName="w-4 h-4"
                    onClick={onProviderBillClick}
                    aria-label={t("bill.interactions.providerBill.label")}
                  />
                )}
              </Tooltip>
            )}
            {isProviderBookletButtonEnabled && (
              <Tooltip content={t("bill.interactions.providerBooklet.label")}>
                {(props) => (
                  <IconButton
                    {...props}
                    icon={DocumentCheckIcon}
                    iconClassName="w-4 h-4"
                    onClick={onProviderBookletClick}
                    aria-label={t("bill.interactions.providerBooklet.label")}
                  />
                )}
              </Tooltip>
            )}
            {isInsuranceEnabled && (
              <Tooltip
                content={t(
                  `bill.interactions.chubbInsurance.${isInsuranceAvailable ? "available" : "unavailable"}.label`,
                )}
              >
                {(props) => (
                  <IconButton
                    {...props}
                    isLoading={isInsuranceAvailable && isInsuranceFilesLoading}
                    icon={
                      isInsuranceAvailable
                        ? ShieldIcon
                        : ShieldStrikethroughIcon
                    }
                    iconClassName={twMerge(
                      "w-4 h-4",
                      !isInsuranceAvailable && "cursor-default",
                    )}
                    onClick={
                      isInsuranceAvailable ? onInsuranceButtonClick : undefined
                    }
                  />
                )}
              </Tooltip>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
